import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { cn } from '@/utils/cn';

interface Option {
    opt_id: string | number;
    opt_name: string;
    count?: number;
}

interface FSelectProps {
    options: Option[];
    value: string[];
    onChange: (selectedValues: string[]) => void;
    placeholder?: string;
    maxHeight?: string;
    className?: string;
    searchable?: boolean;
    showCount?: boolean;
    dropdownClassName?: string;
    wideDropdown?: boolean;
}

const FSelect: React.FC<FSelectProps> = ({
    options = [],
    value = [],
    onChange,
    placeholder = "Select options...",
    className = "",
    searchable = true,
    showCount = false,
    dropdownClassName = "",
    wideDropdown = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    // Open upwards when the menu would run past the bottom of its scroll area (e.g. the last
    // filter in a side panel), where it was cut off and unreachable.
    const [openUp, setOpenUp] = useState(false);

    useLayoutEffect(() => {
        if (!isOpen) { setOpenUp(false); return; }
        const trigger = dropdownRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) return;
        let limitTop = 0;
        let limitBottom = window.innerHeight;
        for (let el = trigger.parentElement; el; el = el.parentElement) {
            const { overflowY } = getComputedStyle(el);
            if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden') {
                const r = el.getBoundingClientRect();
                limitTop = Math.max(limitTop, r.top);
                limitBottom = Math.min(limitBottom, r.bottom);
                break;
            }
        }
        const t = trigger.getBoundingClientRect();
        const h = menu.offsetHeight;
        const spaceBelow = limitBottom - t.bottom;
        const spaceAbove = t.top - limitTop;
        setOpenUp(spaceBelow < h + 8 && spaceAbove > spaceBelow);
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, searchable]);

    // Filter and deduplicate options based on search term
    // Deduplicate by opt_name (keep first occurrence)
    const uniqueOptions = options.reduce((acc, option) => {
        const exists = acc.some(opt => opt.opt_name === option.opt_name);
        if (!exists) {
            acc.push(option);
        }
        return acc;
    }, [] as Option[]);

    const filteredOptions = uniqueOptions.filter(option =>
        option.opt_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Get option name by ID - handle both opt_id and uuid for backward compatibility
    const getOptionNameById = (optionId: string) => {
        const option = options.find(opt =>
            String(opt.opt_id) === optionId ||
            (opt as any).uuid === optionId
        );
        return option ? option.opt_name : optionId;
    };

    // Handle option toggle
    const handleOptionToggle = (optionValue: string) => {
        const currentValues = Array.isArray(value) ? value : [];
        const newValue = currentValues.includes(optionValue)
            ? currentValues.filter(v => v !== optionValue)
            : [...currentValues, optionValue];
        onChange(newValue);
    };

    // Handle select all filtered options
    const handleSelectAll = () => {
        const allFilteredValues = filteredOptions.map(opt =>
            String(opt.opt_id) || (opt as any).uuid
        );
        const newValue = [...new Set([...value, ...allFilteredValues])];
        onChange(newValue);
    };

    // Handle clear all
    const handleClearAll = () => {
        onChange([]);
    };

    // Remove individual selected item
    const removeItem = (itemToRemove: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter(v => v !== itemToRemove));
    };

    return (
        <div className={cn("relative w-full", className)} ref={dropdownRef}>
            {/* Main Input */}
            <div
                className="flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md cursor-pointer hover:border-gray-400 focus-within:ring-2 focus-within:ring-brandColor focus-within:border-transparent bg-white min-h-[40px]"
                onClick={() => setIsOpen(!isOpen)}
            >
                <div className="flex-1 flex items-center gap-1 overflow-hidden">
                    {value.length === 0 ? (
                        <span className="text-gray-400 truncate">{placeholder}</span>
                    ) : value.length <= 2 ? (
                        <div className="flex gap-1 overflow-hidden">
                            {value.map(item => {
                                const displayName = getOptionNameById(item);
                                return (
                                    <span
                                        key={item}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-brandColor/10 text-brandColor rounded text-sm truncate max-w-[80px]"
                                        title={displayName}
                                    >
                                        <span className="truncate">{displayName}</span>
                                        <X
                                            className="h-3 w-3 cursor-pointer hover:text-brandColorHover"
                                            onClick={(e) => removeItem(item, e)}
                                        />
                                    </span>
                                );
                            })}
                        </div>
                    ) : (
                        <span className="text-gray-700 truncate">
                            {value.length} selected
                        </span>
                    )}
                </div>
                <ChevronDown className={cn(
                    "h-4 w-4 text-gray-400 transition-transform",
                    isOpen && "rotate-180"
                )} />
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div ref={menuRef} className={cn(
                    "absolute bg-white border border-gray-200 rounded-md shadow-lg z-50",
                    openUp ? "bottom-full mb-1" : "top-full mt-1",
                    wideDropdown ? "left-0 min-w-max" : "left-0 right-0",
                    dropdownClassName
                )}>
                    {/* Search Input */}
                    {searchable && (
                        <div className="p-2 border-b border-gray-100">
                            <div className="relative">
                                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                                <input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search options..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-7 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    {filteredOptions.length > 0 && (
                        <div className="flex items-center justify-between p-2 border-b border-gray-100 bg-gray-50">
                            <span className="text-xs text-gray-600">
                                {filteredOptions.length} option{filteredOptions.length !== 1 ? 's' : ''}
                            </span>
                            <div className="flex gap-2">
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSelectAll();
                                    }}
                                    className="text-xs text-brandColor hover:text-brandColorHover font-medium"
                                >
                                    Select All
                                </button>
                                {value.length > 0 && (
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleClearAll();
                                        }}
                                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                                    >
                                        Clear All
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Options List */}
                    <div className="max-h-48 overflow-y-auto">
                        {filteredOptions.length === 0 ? (
                            <div className="p-3 text-xs text-gray-500 text-center">
                                {searchTerm ? 'No options found' : 'No options available'}
                            </div>
                        ) : (
                            filteredOptions.map((option, index) => {
                                // Use opt_id field as the actual ID for backend, fallback to uuid for compatibility
                                const optionId = option.opt_id ? String(option.opt_id) : ((option as any).uuid || String(index));
                                const currentValues = Array.isArray(value) ? value : [];
                                const isSelected = currentValues.includes(optionId);
                                return (
                                    <div
                                        key={`${option.opt_id || option.opt_name || index}`}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleOptionToggle(optionId);
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            className="w-4 h-4 text-brandColor border-gray-300 rounded focus:ring-brandColor flex-shrink-0"
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span className="flex-1 text-sm text-gray-900 text-left">
                                            {option.opt_name}
                                        </span>
                                        {showCount && option.count && (
                                            <span className="text-xs text-gray-400">
                                                ({option.count})
                                            </span>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default FSelect;